/**
 * Overview: the operating system, the machine's whole spec sheet, and the agent build.
 *
 * The lines come from `hardware.ts`, which composes each one out of the parts that exist and returns
 * '' when none do. This module's job is the other half of that rule: **an empty line hides its
 * heading, and an empty list hides its whole section.** A Synology reports no display adapter and
 * cannot read its memory modules unprivileged, a VM has no chassis, and a device enrolled before
 * these fields existed has none of them until its next inventory push - so a card of blank headings
 * is the normal case, and it reads as data that failed to load rather than as data the machine did
 * not report.
 *
 * The one place that rule is deliberately broken is the security chip: see `tpmState`.
 */
import { insets, type Control, type StackPanel } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import { AgentPayloads, ManagementChannel, type DeviceDto, type OsInfoDto } from '../../contracts.gen.js';
import { join } from '../../format.js';
import { isWindowsCaption } from '../../platform.js';
import {
  batteryRows,
  biosText,
  chassisText,
  cpuDetailText,
  diskRows,
  gpuRows,
  memoryRows,
  memorySummaryText,
  monitorRows,
  motherboardSerialText,
  motherboardText,
  nicRows,
  systemIdentityText,
  tpmState,
  type BatteryRow,
  type HardwareRow
} from '../../hardware.js';
import { chip } from '../../ui/Chip.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { CrmLabel } from '../../ui/Text.js';
import { button, card, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, splitRow, stackOf, text, value, vstack } from '../../ui/layout.js';
import { compareVersions } from '../devices.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

export function overviewTab(dc: DeviceTabContext): DeviceTab {
  /** The build the server serves for THIS device's flavor. Null = that flavor is unpublished. */
  let served: string | null = null;
  let pushing = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });

  // ---- operating system ----
  const osCaption = value('', { font: FONT.bold, wrap: true });
  const osVersion = muted('');
  const osMachine = muted('');
  const osCard = card();
  osCard.controls.add(stackOf(vstack({ spacing: GAP.xs }), osCaption, osVersion, osMachine));

  // ---- hardware ----
  const system = value('', { font: FONT.bold, wrap: true });
  const chassis = muted('');
  const identity = muted('');

  const cpuName = value('', { wrap: true });
  const cpuDetail = muted('');
  const cpuSection = section('Processor', cpuName, cpuDetail);

  const memorySummary = muted('');
  const memoryList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const memorySection = section('Memory', memorySummary, memoryList);

  const gpuList = vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false });
  const gpuSection = section('Graphics', gpuList);

  const monitorList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const monitorSection = section(
    'Displays',
    monitorList,
    muted('Only displays that were attached and switched on when the inventory ran.')
  );

  const batteryList = vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false });
  const batterySection = section(
    'Battery',
    batteryList,
    muted(
      'Health is what the pack can still hold against what it was built to hold — not its current charge.'
    )
  );

  const board = value('', { wrap: true });
  const boardSerial = muted('');
  const boardSection = section('Motherboard', board, boardSerial);

  const bios = muted('');
  const biosSection = section('Firmware', bios);

  const tpmChip = chip('', 'ok');
  const tpmDetail = muted('');
  const tpmSection = section(
    'Security chip',
    stackOf(hstack({ spacing: GAP.sm }), tpmChip),
    tpmDetail
  );

  const diskList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const diskSection = section('Disks', diskList);

  const nicList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const nicSection = section('Network', nicList);

  const hardwareCard = card();
  hardwareCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      system,
      chassis,
      identity,
      cpuSection,
      memorySection,
      gpuSection,
      monitorSection,
      batterySection,
      boardSection,
      biosSection,
      tpmSection,
      diskSection,
      nicSection
    )
  );

  // ---- agent ----
  const agentVersion = value('', { font: FONT.bold });
  const upToDate = chip('Up to date', 'ok');
  const outOfDate = chip('', 'warn');
  const pushStatus = new StatusLine();
  const updateNow = button('Update now', () => void pushUpdate(), true);
  const agentRow = inlineRow();
  agentRow.controls.addRange([
    value('Version', { font: FONT.small, role: 'muted' }),
    agentVersion,
    upToDate,
    outOfDate,
    updateNow,
    pushStatus.label
  ]);
  const agentCard = card();
  agentCard.controls.add(agentRow);
  const agentHeading = h2('Agent');

  panel.controls.addRange([
    h2('Operating system'),
    osCard,
    h2('Hardware'),
    hardwareCard,
    agentHeading,
    agentCard
  ]);

  /**
   * Tells this device's agent to install the served build now.
   *
   * The version chip does not move on success: it comes from the device's own inventory, and the
   * agent only reports the new one once its service has restarted on it. So the message says the
   * push happened rather than claiming the update is done.
   */
  async function pushUpdate(): Promise<void> {
    if (pushing) return;
    pushing = true;
    updateNow.enabled = false;
    pushStatus.busy('Pushing…');
    try {
      const version = await crm.pushDeviceAgentUpdate(dc.deviceId);
      if (dc.ctx.cancelled()) return;
      pushStatus.set(
        version === null
          ? 'Push failed — the device may have just gone offline.'
          : `Update ${version} pushed; the agent restarts on the new build shortly.`,
        version === null ? 'error' : 'ok'
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) pushStatus.error(describeError(error));
    } finally {
      pushing = false;
      updateNow.enabled = dc.isOnline();
      dc.relayout();
    }
  }

  function applyOs(os: OsInfoDto | null | undefined): void {
    osCaption.text = os?.caption || 'No inventory collected yet';
    osVersion.text = join([
      os?.version ? `Version ${os.version}` : null,
      os?.build ? `Build ${os.build}` : null,
      os?.architecture
    ]);
    osMachine.text = join([
      os?.machineName ? `Machine ${os.machineName}` : null,
      os?.domain ? `Domain ${os.domain}` : null
    ]);
    osVersion.visible = osVersion.text.length > 0;
    osMachine.visible = osMachine.text.length > 0;
  }

  function applyHardware(device: DeviceDto): void {
    const h = device.hardware;
    system.text = join([h?.manufacturer, h?.model], ' ') || 'Unknown machine';
    setLine(chassis, chassisText(h));
    setLine(identity, systemIdentityText(h));

    cpuName.text = h?.cpuName || '';
    setLine(cpuDetail, cpuDetailText(h));
    cpuSection.visible = cpuName.text.length > 0 || cpuDetail.visible;

    setLine(memorySummary, memorySummaryText(h));
    const modules = memoryRows(h);
    fillRows(memoryList, modules, slotRow);
    memorySection.visible = memorySummary.visible || modules.length > 0;

    const gpus = gpuRows(h);
    fillRows(gpuList, gpus, detailRow);
    gpuSection.visible = gpus.length > 0;

    const monitors = monitorRows(h);
    fillRows(monitorList, monitors, detailRow);
    monitorSection.visible = monitors.length > 0;

    const batteries = batteryRows(h);
    fillRows(batteryList, batteries, batteryRow);
    batterySection.visible = batteries.length > 0;

    setLine(board, motherboardText(h));
    setLine(boardSerial, motherboardSerialText(h));
    boardSection.visible = board.visible;

    setLine(bios, biosText(h));
    biosSection.visible = bios.visible;

    // Null means the agent could not READ the TPM, which is not the same finding as a machine
    // having none - so the section disappears rather than saying "Not detected". See `tpmState`.
    const tpm = tpmState(h);
    tpmSection.visible = tpm !== null;
    if (tpm) {
      tpmChip.text = tpm.state;
      tpmChip.tone = tpm.tone;
      setLine(tpmDetail, tpm.detail);
    }

    const disks = diskRows(h);
    fillRows(diskList, disks, detailRow);
    diskSection.visible = disks.length > 0;

    const nics = nicRows(h);
    fillRows(nicList, nics, detailRow);
    nicSection.visible = nics.length > 0;
  }

  function applyAgent(device: DeviceDto): void {
    const running = device.agentVersion ?? '';
    agentVersion.text = running || 'No agent reporting';

    // Measured against this device's OWN payload flavor: a Synology agent compared with the Windows
    // payload would read as permanently out of date, which is why the chip used to be suppressed for
    // it entirely.
    const behind = !!running && !!served && compareVersions(served, running) > 0;
    upToDate.visible = !!running && !!served && !behind;
    outOfDate.visible = behind;
    outOfDate.text = `Update available → ${served ?? ''}`;
    updateNow.visible = behind;
    updateNow.enabled = dc.isOnline();
  }

  function apply(): void {
    const device = dc.device();
    if (!device) return;
    applyOs(device.os);
    applyHardware(device);
    applyAgent(device);
    // A phone runs no agent of ours; Google's own app is what reports it.
    const phone = device.channel === ManagementChannel.Android;
    agentHeading.visible = !phone;
    agentCard.visible = !phone;
  }

  /** Fetched once - the served build does not change while a page is open. */
  async function loadServedVersion(): Promise<void> {
    const device = dc.device();
    if (!device || device.channel === ManagementChannel.Android) return;
    try {
      served = await crm.getLatestAgentVersion(flavorFor(device), dc.ctx.dispose.signal());
    } catch {
      // Unpublished for that flavor, or an older server. No chip rather than a wrong one.
      served = null;
    }
    if (dc.ctx.cancelled()) return;
    applyAgent(device);
    dc.relayout();
  }

  apply();
  void loadServedVersion();

  return { panel, applyDevice: apply };
}


/** The payload flavor a device updates from. The port of `AgentPayloads.FlavorFor`. */
function flavorFor(device: DeviceDto): string {
  const caption = device.os?.caption ?? '';
  if (isWindowsCaption(caption)) return AgentPayloads.Windows;
  switch (device.os?.architecture ?? '') {
    case 'Arm64':
      return AgentPayloads.SynologyLinuxArm64;
    case 'Arm':
      return AgentPayloads.SynologyLinuxArm;
    default:
      return AgentPayloads.SynologyLinuxX64;
  }
}

/** A bold sub-heading over its own lines, hidden as a unit. */
function section(heading: string, ...children: Control[]): StackPanel {
  return stackOf(
    vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false, visible: false }),
    text(heading, { font: FONT.bold, margin: insets(0, GAP.sm, 0, 0) }),
    ...children
  );
}

/** Sets a line and hides it when it has nothing to say - the rule this whole module turns on. */
function setLine(label: CrmLabel, content: string): void {
  label.text = content;
  label.visible = content.length > 0;
}

function fillRows<T>(
  host: StackPanel,
  rows: readonly T[],
  build: (row: T) => Control
): void {
  host.controls.clear();
  for (const row of rows) host.controls.add(build(row));
  host.visible = rows.length > 0;
}

/** A name over its detail, with the optional third line the part number needs. */
function detailRow(row: HardwareRow): Control {
  const stack = stackOf(
    vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }),
    value(row.name, { wrap: true })
  );
  if (row.detail) stack.controls.add(muted(row.detail));
  if (row.extra) stack.controls.add(muted(row.extra));
  return stack;
}

/**
 * A memory slot: the label in a fixed-width column, the detail beside it.
 *
 * The slot column is what makes eight modules readable as a table rather than as sixteen lines, and
 * it is the one place on this card where alignment carries meaning - a technician reads down it to
 * find the empty socket.
 */
function slotRow(row: HardwareRow): Control {
  const right = stackOf(
    vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }),
    value(row.detail, { wrap: true })
  );
  if (row.extra) right.controls.add(muted(row.extra));

  const line = splitRow({ spacing: GAP.md, align: 'start' });
  line.controls.add(value(row.name, { width: 150, autoWidth: false }));
  fillWith(line, right);
  return line;
}

function batteryRow(row: BatteryRow): Control {
  const heading = inlineRow();
  heading.controls.add(value(row.name));
  if (row.healthText) heading.controls.add(chip(row.healthText, row.tone));

  const stack = stackOf(vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }), heading);
  if (row.detail) stack.controls.add(muted(row.detail));
  return stack;
}
